/**
 * Confirmation before a REAL Bexio write.
 *
 * A create burns a real Bexio document number, so it never happens on a single click:
 * the user must read a summary of exactly what will be sent and confirm it.
 *
 * The confirm button disables itself while the request is in flight — the double-click
 * guard that keeps one intent from becoming two invoices.
 *
 * Focus-safe: the dialog traps nothing complicated, but it does move focus to the
 * confirm button on open, closes on Escape, and restores focus on unmount.
 */
import React, { useEffect, useRef } from "react";

import { formatChf, monthLabel } from "./bexioDraftLabels";

export default function BexioDraftCreateConfirmModal({
  open,
  row,
  preflight,
  year,
  month,
  submitting,
  onCancel,
  onConfirm,
}) {
  const confirmRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    confirmRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previouslyFocused.current?.focus) previouslyFocused.current.focus();
    };
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const positions = preflight?.positions || [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bexio-create-title"
        className="w-full max-w-md rounded-xl border border-input bg-card p-5 shadow-lg"
      >
        <h2 id="bexio-create-title" className="text-base font-semibold text-foreground">
          Bexio-Entwurf erstellen?
        </h2>

        <dl className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Mieter</dt>
            <dd className="text-right text-foreground">{row?.tenantName || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Referenz</dt>
            <dd className="text-right text-foreground">{row?.reference || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Abrechnungsmonat</dt>
            <dd className="text-right text-foreground">{monthLabel(year, month)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted-foreground">Betrag</dt>
            <dd className="text-right font-semibold tabular-nums text-foreground">
              {formatChf(preflight?.total_amount_chf ?? row?.amount)}
            </dd>
          </div>
        </dl>

        {positions.length > 0 && (
          <ul className="mt-3 space-y-1 rounded-lg border border-input bg-muted/50 p-3 text-xs">
            {positions.map((p, i) => (
              <li key={`${p.revenue_type}-${i}`} className="flex justify-between gap-3">
                <span className="min-w-0 text-foreground">{p.text}</span>
                <span className="shrink-0 tabular-nums text-foreground">
                  {formatChf(p.amount_chf)}
                </span>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          Es wird ein echter Rechnungsentwurf in Bexio erstellt.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
          >
            Abbrechen
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Wird erstellt …" : "Entwurf erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}
